import React, { useEffect, useRef, useMemo, useCallback } from 'react';
import { Bookmark, Search } from 'lucide-react';

/**
 * Reusable transcript viewer with synced playback
 * Used in RecordingDetail and RecordingModal
 *
 * Supports two segment formats:
 * - MongoDB (Transcription model): { speakerName, speakerUid, text, startMs, endMs }
 * - S3 (transcriptBuffer): { speaker, speakerUid, text, timestamp } (epoch ms)
 *
 * When recordingCreatedAt + recordingDurationSec are provided, segments are
 * filtered to only those within the recording window and timestamps are
 * converted to relative offsets for video sync.
 */
const TranscriptViewer = ({
  segments = [],
  currentTimeMs = 0,
  bookmarks = [],
  recordingCreatedAt,
  recordingDurationSec,
  recordingStartedAt,
  onSeek,
  onBookmarkClick,
  searchable = true,
}) => {
  const [searchQuery, setSearchQuery] = React.useState('');
  const activeSegmentRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const userScrolledRef = useRef(false);
  const scrollTimeoutRef = useRef(null);

  // Normalize segments: convert epoch → relative, filter to recording window
  const normalizedSegments = useMemo(() => {
    if (!segments.length) return [];

    // Detect if timestamps are epoch ms (> year 2000 in ms) vs already relative
    const firstTs = segments[0]?.startMs ?? segments[0]?.timestamp ?? 0;
    const isEpoch = firstTs > 946684800000; // Jan 1 2000 in ms

    if (!isEpoch) {
      // Already relative timestamps (MongoDB Transcription format)
      return segments.map((seg, idx) => {
        const speakerName = seg.speakerName || seg.speaker || `Speaker ${seg.speakerUid || idx}`;
        const speakerUid = String(seg.speakerUid || idx);
        const startMs = seg.startMs ?? 0;
        const endMs = seg.endMs ?? (segments[idx + 1]?.startMs || startMs + 5000);
        return { speakerName, speakerUid, text: seg.text, startMs, endMs };
      });
    }

    // Epoch timestamps — need conversion + filtering
    //
    // Two semantics for Recording.createdAt depending on how it was created:
    //   - mix / web mode   → Recording.create() runs in stopRecording, so
    //     createdAt ≈ stop time. recStartMs = createdAt - duration.
    //   - worker-recorder  → Recording.create() runs in startRecording, so
    //     createdAt ≈ start time. Using createdAt - duration as start is
    //     WRONG and pushes the filter window back into pre-meeting territory,
    //     excluding every real segment.
    //
    // Prefer the dedicated `recordingStartedAt` field (both flows populate
    // it explicitly with the true start time). Fall back to the old math
    // only when it's missing (very old recordings predating that field).
    const recStartedExplicit = recordingStartedAt
      ? new Date(recordingStartedAt).getTime()
      : null;
    const recCreatedMs = recordingCreatedAt
      ? new Date(recordingCreatedAt).getTime()
      : null;
    const recStartMs = recStartedExplicit
      ?? ((recCreatedMs && recordingDurationSec)
        ? recCreatedMs - recordingDurationSec * 1000
        : null);
    const recEndMs = (recStartMs && recordingDurationSec)
      ? recStartMs + recordingDurationSec * 1000
      : recCreatedMs; // last-resort fallback

    // Filter to only segments within the recording window (with 10s grace)
    const grace = 10000;
    const filtered = recStartMs
      ? segments.filter(seg => {
          const ts = seg.startMs ?? seg.timestamp ?? 0;
          return ts >= (recStartMs - grace) && (!recEndMs || ts <= recEndMs + grace);
        })
      : segments;

    // If filtering removed everything, fall back to showing all segments
    // (recording may have started before STT, or timestamps are off)
    const source = filtered.length > 0 ? filtered : segments;

    // Use recording start time or first filtered segment as baseline
    const baselineMs = recStartMs || (source[0]?.startMs ?? source[0]?.timestamp ?? 0);

    return source.map((seg, idx) => {
      const speakerName = seg.speakerName || seg.speaker || `Speaker ${seg.speakerUid || idx}`;
      const speakerUid = String(seg.speakerUid || idx);

      const rawStart = seg.startMs ?? seg.timestamp ?? 0;
      const startMs = Math.max(0, rawStart - baselineMs);

      const nextRawStart = source[idx + 1]?.startMs ?? source[idx + 1]?.timestamp;
      const rawEnd = seg.endMs ?? (nextRawStart || rawStart + 5000);
      const endMs = Math.max(0, rawEnd - baselineMs);

      return { speakerName, speakerUid, text: seg.text, startMs, endMs };
    });
  }, [segments, recordingCreatedAt, recordingDurationSec]);

  // Pause auto-scroll when user scrolls manually, resume after 5s
  const handleUserScroll = useCallback(() => {
    userScrolledRef.current = true;
    if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    scrollTimeoutRef.current = setTimeout(() => {
      userScrolledRef.current = false;
    }, 5000);
  }, []);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    container.addEventListener('wheel', handleUserScroll, { passive: true });
    container.addEventListener('touchmove', handleUserScroll, { passive: true });
    return () => {
      container.removeEventListener('wheel', handleUserScroll);
      container.removeEventListener('touchmove', handleUserScroll);
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    };
  }, [handleUserScroll]);

  // Auto-scroll to active segment (only if user hasn't scrolled recently)
  // Uses manual scrollTop instead of scrollIntoView to avoid scrolling the whole page
  useEffect(() => {
    const container = scrollContainerRef.current;
    const activeEl = activeSegmentRef.current;
    if (!container || !activeEl || userScrolledRef.current) return;

    const containerRect = container.getBoundingClientRect();
    const elRect = activeEl.getBoundingClientRect();
    const elOffsetInContainer = elRect.top - containerRect.top + container.scrollTop;
    const targetScroll = elOffsetInContainer - containerRect.height / 2 + elRect.height / 2;

    container.scrollTo({ top: targetScroll, behavior: 'smooth' });
  }, [currentTimeMs]);

  // Filter segments by search
  const filteredSegments = normalizedSegments.filter(seg =>
    !searchQuery || seg.text.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Build stable speaker color map (assigned in order of first appearance)
  const speakerColorMap = useMemo(() => {
    const SPEAKER_COLORS = [
      { text: 'text-blue-400',   dot: 'bg-blue-400' },
      { text: 'text-emerald-400', dot: 'bg-emerald-400' },
      { text: 'text-purple-400', dot: 'bg-purple-400' },
      { text: 'text-amber-400',  dot: 'bg-amber-400' },
      { text: 'text-pink-400',   dot: 'bg-pink-400' },
      { text: 'text-cyan-400',   dot: 'bg-cyan-400' },
      { text: 'text-orange-400', dot: 'bg-orange-400' },
      { text: 'text-lime-400',   dot: 'bg-lime-400' },
      { text: 'text-rose-400',   dot: 'bg-rose-400' },
      { text: 'text-teal-400',   dot: 'bg-teal-400' },
    ];
    const map = new Map();
    let idx = 0;
    for (const seg of normalizedSegments) {
      const key = seg.speakerName;
      if (!map.has(key)) {
        map.set(key, SPEAKER_COLORS[idx % SPEAKER_COLORS.length]);
        idx++;
      }
    }
    return map;
  }, [normalizedSegments]);

  const getSpeakerColor = (speakerName) => {
    return speakerColorMap.get(speakerName) || { text: 'text-white/70', dot: 'bg-white/70' };
  };

  // Format timestamp as mm:ss
  const formatTime = (ms) => {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      {/* Search bar */}
      {searchable && (
        <div className="mb-3 relative flex-shrink-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <input
            type="text"
            placeholder="Search transcript..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-[#6a1fbf]/50"
          />
        </div>
      )}

      {/* Transcript segments */}
      <div ref={scrollContainerRef} className="flex-1 min-h-0 overflow-y-auto space-y-2 pr-1">
        {filteredSegments.length === 0 ? (
          <p className="text-white/50 text-sm text-center py-8">
            {searchQuery ? 'No matching segments found' : 'No transcript available'}
          </p>
        ) : (
          filteredSegments.map((segment, idx) => {
            const isActive = currentTimeMs >= segment.startMs && currentTimeMs <= segment.endMs;
            const hasBookmark = bookmarks.some(
              b => b.timestampMs >= segment.startMs && b.timestampMs <= segment.endMs
            );

            return (
              <div
                key={`${segment.speakerUid}-${segment.startMs}-${idx}`}
                ref={isActive ? activeSegmentRef : null}
                onClick={() => onSeek && onSeek(segment.startMs)}
                className={`
                  p-3 rounded-lg cursor-pointer transition-all duration-200
                  ${isActive
                    ? 'bg-[#6a1fbf]/20 border-l-2 border-[#6a1fbf]'
                    : 'bg-white/5 hover:bg-white/10 border-l-2 border-transparent'
                  }
                `}
              >
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${getSpeakerColor(segment.speakerName).dot}`} />
                    <span className={`text-xs font-medium truncate ${getSpeakerColor(segment.speakerName).text}`}>
                      {segment.speakerName}
                    </span>
                    <span className="text-[10px] text-white/40 flex-shrink-0">
                      {formatTime(segment.startMs)}
                    </span>
                  </div>
                  {hasBookmark && (
                    <Bookmark className="w-3 h-3 text-yellow-400 fill-yellow-400 flex-shrink-0" />
                  )}
                </div>
                <p className="text-sm text-white/90 leading-relaxed break-words">
                  {searchQuery ? (
                    <HighlightedText text={segment.text} query={searchQuery} />
                  ) : (
                    segment.text
                  )}
                </p>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

// Helper component to highlight search matches
const HighlightedText = ({ text, query }) => {
  if (!query) return text;

  const parts = text.split(new RegExp(`(${query})`, 'gi'));
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === query.toLowerCase() ? (
          <span key={i} className="bg-yellow-400/30 text-yellow-300">
            {part}
          </span>
        ) : (
          part
        )
      )}
    </>
  );
};

export default TranscriptViewer;
